import React, { useEffect, useState } from 'react';
import api from '../../../utils/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { fmtMoney, fmtPeriodo } from './kpiUtils';
import { Loader2, DollarSign, Users, ChevronDown, ChevronRight, Eye, TrendingUp, Download, FileText } from 'lucide-react';

const ComposicionMesModal = ({ mes, open, onClose, onVerPrestamo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(null);

  const handleExport = async (formato) => {
    try {
      setExporting(formato);
      const r = await api.get(`/kpis/prestamos/composicion/${mes}/export`, { params: { formato }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `composicion_${mes}.${formato === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar la composición');
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    if (!open || !mes) return;
    let cancel = false;
    setLoading(true);
    setData(null);
    setExpanded(null);
    api.get(`/kpis/prestamos/composicion/${mes}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) { toast.error(e.response?.data?.detail || 'No se pudo cargar la composición del mes'); onClose?.(); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, mes, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="composicion-mes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded-full bg-emerald-600"><DollarSign className="text-white" size={16} /></div>
            Composición de {mes ? fmtPeriodo(mes) : ''}
          </DialogTitle>
          <DialogDescription>Quiénes pagaron ese mes y cuánto aportaron a la ganancia realizada.</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Cargando…</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400" data-testid="composicion-mes-vacio">No hubo cobros que aporten ganancia en {fmtPeriodo(mes)}.</div>
        ) : (
          <div className="space-y-4 min-w-0 w-full">
            {/* Toolbar export */}
            <div className="flex items-center justify-end gap-2" data-testid="composicion-export-toolbar">
              <Button size="sm" variant="outline" className="h-8" disabled={!!exporting}
                onClick={() => handleExport('excel')} data-testid="composicion-export-excel">
                {exporting === 'excel' ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Download className="mr-1.5" size={14} />}
                Excel
              </Button>
              <Button size="sm" variant="outline" className="h-8" disabled={!!exporting}
                onClick={() => handleExport('pdf')} data-testid="composicion-export-pdf">
                {exporting === 'pdf' ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <FileText className="mr-1.5" size={14} />}
                PDF
              </Button>
            </div>
            {/* Resumen del mes */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3" data-testid="composicion-total-ganancia">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-medium flex items-center gap-1"><TrendingUp size={12} /> Ganancia del mes</p>
                <p className="text-lg font-bold font-mono text-emerald-700 tabular-nums mt-0.5">{fmtMoney(data.total_ganancia)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="composicion-total-pagado">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1"><DollarSign size={12} /> Total cobrado</p>
                <p className="text-lg font-bold font-mono text-slate-700 tabular-nums mt-0.5">{fmtMoney(data.total_pagado)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="composicion-cantidad-personas">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1"><Users size={12} /> Personas</p>
                <p className="text-lg font-bold font-mono text-slate-700 tabular-nums mt-0.5">{data.cantidad_personas}</p>
              </div>
            </div>

            {/* Tabla de personas */}
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="composicion-tabla">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500">
                      <th className="text-left font-medium px-3 py-2 w-6"></th>
                      <th className="text-left font-medium px-3 py-2">Persona</th>
                      <th className="text-left font-medium px-3 py-2">Frecuencia</th>
                      <th className="text-right font-medium px-3 py-2">Pagó</th>
                      <th className="text-right font-medium px-3 py-2">Ganancia</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it) => {
                      const isOpen = expanded === it.prestamo_id;
                      return (
                        <React.Fragment key={it.prestamo_id}>
                          <tr className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`composicion-fila-${it.prestamo_id}`}
                              onClick={() => setExpanded(isOpen ? null : it.prestamo_id)}>
                            <td className="px-3 py-2 text-slate-400">{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                            <td className="px-3 py-2">
                              <span className="text-slate-700 font-medium">{it.nombre || 'Sin nombre'}</span>
                              <span className="block text-[11px] font-mono text-slate-400">{it.cuit} · #{it.prestamo_id}</span>
                            </td>
                            <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px] font-normal">{it.frecuencia || '—'}</Badge></td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtMoney(it.pagado_mes)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700" data-testid={`composicion-ganancia-${it.prestamo_id}`}>{fmtMoney(it.ganancia_mes)}</td>
                            <td className="px-3 py-2 text-right">
                              {onVerPrestamo && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-emerald-700"
                                  onClick={(e) => { e.stopPropagation(); onVerPrestamo(it.prestamo_id); }} data-testid={`composicion-ver-${it.prestamo_id}`}>
                                  <Eye size={14} />
                                </Button>
                              )}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-slate-50/70" data-testid={`composicion-pagos-${it.prestamo_id}`}>
                              <td></td>
                              <td colSpan={5} className="px-3 py-2">
                                <div className="space-y-1">
                                  {(it.pagos || []).map((p, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">
                                        <span className="font-mono">{p.fecha}</span>
                                        <span className="mx-1.5 text-slate-300">·</span>
                                        {p.es_punitorio ? <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] px-1.5 py-0">Punitorios</Badge> : <span>Cuota {p.cuota}</span>}
                                        {p.imputado && <span className="ml-1.5 text-slate-400 italic">(sin pago registrado)</span>}
                                      </span>
                                      <span className="text-slate-600 tabular-nums">
                                        {p.monto > 0 && <span className="mr-3">Pagó {fmtMoney(p.monto)}</span>}
                                        <span className="text-emerald-700 font-medium">+{fmtMoney(p.ganancia)}</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-emerald-200 bg-emerald-50/40 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-emerald-800">Total del mes</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtMoney(data.total_pagado)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid="composicion-total-fila">{fmtMoney(data.total_ganancia)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComposicionMesModal;
